import { useEffect, useState } from 'react'
import { Bell, Bookmark, Clapperboard, Download, DownloadCloud, Film, LibraryBig, LogOut, Moon, RadioTower, Settings, Sun, UserRound } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom'
import { CatalogView } from './features/catalog/CatalogView'
import { DownloadsView } from './features/downloads/DownloadsView'
import { SubscriptionsView } from './features/subscriptions/SubscriptionsView'
import { SettingsView } from './features/settings/SettingsView'
import { NewMoviesView } from './features/discoveries/NewMoviesView'
import { SeriesView } from './features/series/SeriesView'
import { NotificationCenter } from './components/NotificationCenter'
import { StorageCapacity } from './components/StorageCapacity'
import { api, type AppNotification, type DownloadJob, type SystemStatus } from './lib/api'
import { useEventStream } from './lib/useEventStream'
import { useAuth } from './auth/AuthContext'
import { usePwaInstall } from './lib/pwa'

type ThemeMode = 'system' | 'dark' | 'light'

export default function App() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const pwa = usePwaInstall()
  const location = useLocation()
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem('mv-theme')
    return stored === 'dark' || stored === 'light' ? stored : 'system'
  })
  const [resolvedTheme, setResolvedTheme] = useState<'dark' | 'light'>(() => window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  useEventStream()
  const status = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api<SystemStatus>('/api/v1/system/status'),
    refetchInterval: 60_000,
  })
  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<AppNotification[]>('/api/v1/notifications?limit=100'),
  })
  const downloads = useQuery({
    queryKey: ['downloads'],
    queryFn: () => api<DownloadJob[]>('/api/v1/downloads?limit=1000'),
    refetchInterval: 15_000,
  })
  const unread = notifications.data?.filter((notification) => !notification.read).length ?? 0
  const jobs = downloads.data?.filter((job) => job.state !== 'DELETED' && job.state !== 'SKIPPED') ?? []
  const completedDownloads = jobs.filter((job) => job.state === 'COMPLETED').length
  const downloadCount = `${completedDownloads}/${jobs.length}`
  const downloadLabel = `${t('nav.downloads')} (${downloadCount})`
  const routeHeadings: Record<string, { label: string; icon: React.ReactNode; meta?: string }> = {
    '/neu': { label: t('nav.newMovies'), icon: <Film /> },
    '/serien': { label: t('nav.series'), icon: <LibraryBig /> },
    '/sendungen': { label: t('nav.films'), icon: <Clapperboard /> },
    '/merkliste': { label: t('nav.bookmarks'), icon: <Bookmark /> },
    '/downloads': { label: t('nav.downloads'), icon: <Download />, meta: downloadCount },
    '/abos': { label: t('nav.subscriptions'), icon: <RadioTower /> },
    '/einstellungen': { label: t('nav.settings'), icon: <Settings /> },
  }
  const routeHeading = routeHeadings[location.pathname]
    ?? Object.entries(routeHeadings).find(([path]) => location.pathname.startsWith(`${path}/`))?.[1]
    ?? routeHeadings['/neu']
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      const resolved = theme === 'system' ? (media.matches ? 'dark' : 'light') : theme
      document.documentElement.dataset.theme = resolved
      document.documentElement.dataset.themePreference = theme
      setResolvedTheme(resolved)
    }
    apply()
    media.addEventListener('change', apply)
    localStorage.setItem('mv-theme', theme)
    return () => media.removeEventListener('change', apply)
  }, [theme])

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark"><img src="/meine-mediathek-logo.png" alt="" /></div>
        <div><strong>{t('app.name')}</strong><span>{t('app.tagline')}</span></div>
      </div>
      <nav>
        <NavButton to="/neu" icon={<Film />} label={t('nav.newMovies')} />
        <NavButton to="/serien" icon={<LibraryBig />} label={t('nav.series')} />
        <NavButton to="/sendungen" icon={<Clapperboard />} label={t('nav.films')} />
        <NavButton to="/merkliste" icon={<Bookmark />} label={t('nav.bookmarks')} />
        <NavButton to="/downloads" icon={<Download />} label={downloadLabel} />
        <NavButton to="/abos" icon={<RadioTower />} label={t('nav.subscriptions')} />
        <NavButton to="/einstellungen" icon={<Settings />} label={t('nav.settings')} />
      </nav>
      <StorageCapacity roots={status.data?.mediaRoots ?? []} />
    </aside>
    <main className="main-area">
      <header className="topbar">
        <div className="topbar-heading">{routeHeading.icon}<strong>{routeHeading.label}</strong>{routeHeading.meta && <span className="topbar-heading-meta" title={t('downloads.countMeaning')}>{routeHeading.meta}</span>}</div>
        <div className="topbar-actions">
          {pwa.available && <button className="topbar-install" onClick={pwa.install} title={t('pwa.install')}
            aria-label={t('pwa.install')}><DownloadCloud /><span>{t('pwa.install')}</span></button>}
          <button className="theme-toggle" onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            title={resolvedTheme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
            aria-label={resolvedTheme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}>
            <span className={`theme-toggle-knob ${resolvedTheme}`}>{resolvedTheme === 'dark' ? <Moon /> : <Sun />}</span>
          </button>
          <button className="notification-button" onClick={() => setNotificationsOpen(true)} aria-label={t('notifications.open')}>
            <Bell />{unread > 0 && <span>{unread > 99 ? '99+' : unread}</span>}
          </button>
          <div className="topbar-user"><UserRound /><span>{user?.username}</span></div>
          <button className="notification-button" onClick={logout} title={t('auth.signOut')} aria-label={t('auth.signOut')}>
            <LogOut />
          </button>
        </div>
      </header>
      <div className="view-container">
        <Routes>
          <Route path="/sendungen" element={<CatalogView mediaRoots={status.data?.mediaRoots ?? []} />} />
          <Route path="/sendungen/:filmId" element={<CatalogView mediaRoots={status.data?.mediaRoots ?? []} />} />
          <Route path="/neu" element={<NewMoviesView mediaRoots={status.data?.mediaRoots ?? []} />} />
          <Route path="/neu/:eventId" element={<NewMoviesView mediaRoots={status.data?.mediaRoots ?? []} />} />
          <Route path="/serien" element={<SeriesView mediaRoots={status.data?.mediaRoots ?? []} />} />
          <Route path="/serien/:seriesId" element={<SeriesView mediaRoots={status.data?.mediaRoots ?? []} />} />
          <Route path="/merkliste" element={<CatalogView mediaRoots={status.data?.mediaRoots ?? []} bookmarkView />} />
          <Route path="/merkliste/:filmId" element={<CatalogView mediaRoots={status.data?.mediaRoots ?? []} bookmarkView />} />
          <Route path="/downloads" element={<DownloadsView mediaRoots={status.data?.mediaRoots ?? []} />} />
          <Route path="/abos" element={<SubscriptionsView mediaRoots={status.data?.mediaRoots ?? []} />} />
          <Route path="/einstellungen" element={<SettingsView />} />
          <Route path="/" element={<Navigate to="/neu" replace />} />
          <Route path="*" element={<Navigate to="/neu" replace />} />
        </Routes>
      </div>
    </main>
    <NotificationCenter open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
  </div>
}

function NavButton({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return <NavLink to={to} className={({ isActive }) => `nav-button ${isActive ? 'active' : ''}`}>{icon}<span>{label}</span></NavLink>
}
